/**
 * Rankbist's own visitor analytics + consent (Phase 20).
 *
 * - Without analytics consent only an anonymous page view is sent: no ids,
 *   nothing stored in the browser.
 * - With analytics consent a browser id and a visit id (30 minutes without
 *   activity ends a visit) are kept in localStorage, and page views, clicks
 *   and named events are sent to POST /api/t/collect.
 * - Marketing consent additionally lets the ad pixels load (lib/pixels.ts).
 *
 * Named events (track("signup") …) also go to PostHog and the ad pixels that
 * are allowed, with the same event id the server uses, so platforms count a
 * conversion once.
 */

import { getToken } from "./api";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8000";
const CONSENT_KEY = "rb_consent";
const AID_KEY = "rb_aid";
const SID_KEY = "rb_sid";
const LAST_KEY = "rb_slast";
const CTX_KEY = "rb_sctx";
const SESSION_GAP = 30 * 60 * 1000;
const CLICK_IDS = ["gclid", "gbraid", "wbraid", "fbclid", "ttclid", "msclkid", "li_fat_id", "twclid", "rdt_cid"];
const UTM = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

export interface Consent {
  v: 1;
  analytics: boolean;
  marketing: boolean;
  ts: string;
}

type Ev = { t: "pageview" | "click" | "event"; p: string; ti?: string; l?: string; h?: string; n?: string; pr?: Record<string, unknown> };

const listeners = new Set<(c: Consent | null) => void>();
const queue: Ev[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function ls(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

// The choice also lives in memory and in a small cookie, so the banner can't
// keep coming back when this browser blocks or wipes localStorage.
let memConsent: Consent | null = null;
const CONSENT_COOKIE = "rb_consent";

function parseConsent(raw: string | null | undefined): Consent | null {
  try {
    const c = raw ? (JSON.parse(raw) as Consent) : null;
    return c && c.v === 1 ? c : null;
  } catch {
    return null;
  }
}

function cookieConsent(): Consent | null {
  try {
    const m = document.cookie.match(/(?:^|;\s*)rb_consent=([^;]*)/);
    return m ? parseConsent(decodeURIComponent(m[1])) : null;
  } catch {
    return null;
  }
}

export function getConsent(): Consent | null {
  if (typeof window === "undefined") return null;
  let stored: Consent | null = null;
  try {
    stored = parseConsent(ls()?.getItem(CONSENT_KEY));
  } catch {
    stored = null;
  }
  return stored ?? memConsent ?? cookieConsent();
}

export function setConsent(analytics: boolean, marketing: boolean) {
  const c: Consent = { v: 1, analytics, marketing, ts: new Date().toISOString() };
  memConsent = c;
  try {
    ls()?.setItem(CONSENT_KEY, JSON.stringify(c));
  } catch {
    /* private mode */
  }
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(c))}; Max-Age=${60 * 60 * 24 * 365}; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* cookies blocked */
  }
  if (!analytics) {
    // withdrawing consent removes the ids
    try {
      [AID_KEY, SID_KEY, LAST_KEY].forEach((k) => ls()?.removeItem(k));
      sessionStorage.removeItem(CTX_KEY);
    } catch {
      /* private mode */
    }
  }
  // one listener failing must never stop the others (or the banner closing)
  listeners.forEach((fn) => {
    try {
      fn(c);
    } catch {
      /* ignore */
    }
  });
}

export function onConsent(fn: (c: Consent | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function rid(): string {
  const a = new Uint8Array(12);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Browser id and visit id; created only with analytics consent. */
function ids(): { a: string; s: string; fresh: boolean } | null {
  if (!getConsent()?.analytics) return null;
  const store = ls();
  if (!store) return null;
  let a = store.getItem(AID_KEY);
  if (!a) {
    a = rid();
    store.setItem(AID_KEY, a);
  }
  const now = Date.now();
  let s = store.getItem(SID_KEY);
  const last = Number(store.getItem(LAST_KEY) || 0);
  let fresh = false;
  if (!s || now - last > SESSION_GAP) {
    s = rid();
    store.setItem(SID_KEY, s);
    fresh = true;
    try {
      sessionStorage.removeItem(CTX_KEY);
    } catch {
      /* ignore */
    }
  }
  store.setItem(LAST_KEY, String(now));
  return { a, s, fresh };
}

/** Where this visit came from: referrer, UTM tags, ad click ids. Fixed for the visit. */
function context(): Record<string, string> {
  try {
    const saved = sessionStorage.getItem(CTX_KEY);
    if (saved) return JSON.parse(saved);
  } catch {
    /* ignore */
  }
  const url = new URL(window.location.href);
  const x: Record<string, string> = {
    landing: url.pathname,
    ref: document.referrer || "",
    lang: navigator.language || "",
    screen: `${window.screen.width}x${window.screen.height}`,
  };
  for (const k of [...UTM, ...CLICK_IDS]) {
    const v = url.searchParams.get(k);
    if (v) x[k] = v.slice(0, 200);
  }
  try {
    sessionStorage.setItem(CTX_KEY, JSON.stringify(x));
  } catch {
    /* ignore */
  }
  return x;
}

function flush() {
  timer = null;
  if (!queue.length || typeof window === "undefined") return;
  const events = queue.splice(0, 50);
  const id = ids();
  const body = id ? { c: 1, a: id.a, s: id.s, x: context(), e: events } : { c: 0, e: events.filter((e) => e.t === "pageview") };
  if (!body.e.length) return;
  const token = getToken();
  try {
    void fetch(`${API_BASE}/api/t/collect`, {
      method: "POST",
      keepalive: true,
      headers: token ? { "Content-Type": "text/plain", Authorization: `Bearer ${token}` } : { "Content-Type": "text/plain" },
      body: JSON.stringify(body),
    }).catch(() => {});
  } catch {
    /* never break the page */
  }
  if (queue.length) schedule(0);
}

function schedule(ms = 1500) {
  if (timer) return;
  timer = setTimeout(flush, ms);
}

function push(e: Ev, soon = false) {
  queue.push(e);
  if (queue.length >= 20) flush();
  else schedule(soon ? 50 : 1500);
}

export function pageview() {
  push({ t: "pageview", p: window.location.pathname, ti: document.title.slice(0, 200) }, true);
  window.dispatchEvent(new CustomEvent("rb:pageview"));
}

export function click(label: string, href: string) {
  push({ t: "click", p: window.location.pathname, l: label.slice(0, 160), h: href.slice(0, 300) });
}

/** A named event: signup, checkout_started, purchase, upgrade, … */
export function track(name: string, props: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  push({ t: "event", p: window.location.pathname, n: name, pr: props }, true);
  window.dispatchEvent(new CustomEvent("rb:event", { detail: { name, props } }));
}

export function flushNow() {
  if (timer) clearTimeout(timer);
  flush();
}

/** The browser id, for PostHog's distinct id before sign-in. */
export function browserId(): string | null {
  return getConsent()?.analytics ? ls()?.getItem(AID_KEY) ?? null : null;
}

/** The account id inside a sign-in token (its "sub"), used only as the
 *  event id that lets ad platforms match the browser and server sign-up
 *  events. Never sent anywhere on its own. */
export function userIdFromToken(token: string): string | undefined {
  try {
    const part = token.split(".")[1];
    if (!part) return undefined;
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "="));
    const sub = JSON.parse(json).sub;
    return typeof sub === "string" ? sub : undefined;
  } catch {
    return undefined;
  }
}
